// client/src/pages/OrderCardsPage.jsx
// Wave-9. Manager fills this form to order printed Nedarim cards for a
// camp. Logic mirrors the Nedarim Plus OrderCard flow:
//   - School identity radio (name/logo/none)
//   - Cardholder fields toggles (name/class/phone)
//   - Quantities (named + spare); total must be ≥ 10
//   - When "print name" is on, named-qty MUST equal the count of
//     students without an assigned cardNumber (server enforces; we
//     mirror the validation in the UI so the user can fix it before
//     hitting submit)
//   - Shipping (pickup vs express); express requires address block
//   - Prospectus add-on (₪0.70/unit)
//   - Live total with VAT note
//
// On submit the page posts multipart (logo file rides along) to
// /api/camps/:campId/card-orders. The server saves the order and
// emails office@nedar.im + the submitting manager.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCamp } from '../hooks/useCamps.js';
import {
  useCardOrders,
  useCreateCardOrder,
  useUnprintedStudentCount,
} from '../hooks/useCardOrders.js';
import { formatDateTime } from '../lib/format.js';
import { ApiError } from '../lib/api.js';
import Spinner from '../components/Spinner.jsx';
import {
  CreditCardIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '../components/Icons.jsx';

const PRICE_PER_CARD   = 2;   // ₪
const SHIPPING_EXPRESS = 60;  // ₪
const MIN_TOTAL        = 10;

export default function OrderCardsPage() {
  const { campId } = useParams();
  const camp = useCamp(campId);
  const unprintedQ = useUnprintedStudentCount(campId);
  const ordersQ    = useCardOrders(campId);
  const createMut  = useCreateCardOrder(campId);

  // Form state
  const [schoolDisplay, setSchoolDisplay] = useState('');     // 'NAME' | 'LOGO' | 'NONE'
  const [schoolName, setSchoolName]       = useState('');
  const [logoFile, setLogoFile]           = useState(null);

  // Master toggle: are the cards personalised per-student at all? Only
  // when "YES" do the field checkboxes (name / class / phone) appear.
  // "NO" = generic blank cards, all print flags off.
  const [personalize, setPersonalize] = useState(''); // '' | 'YES' | 'NO'
  const [printName, setPrintName]   = useState(true);  // default-on once personalising
  const [printClass, setPrintClass] = useState(false);
  const [printPhone, setPrintPhone] = useState(false);

  const [qtyNamed, setQtyNamed] = useState('');
  const [qtySpare, setQtySpare] = useState('');

  const [inquiryPhone, setInquiryPhone] = useState('');

  const [shippingMethod, setShippingMethod]       = useState(''); // 'PICKUP' | 'EXPRESS'
  const [shippingAddress, setShippingAddress]     = useState('');
  const [shippingCity, setShippingCity]           = useState('');
  const [shippingRecipient, setShippingRecipient] = useState('');
  const [shippingPhone, setShippingPhone]         = useState('');
  const [shippingPhone2, setShippingPhone2]       = useState('');
  const [shippingDoorOk, setShippingDoorOk]       = useState(''); // 'YES' | 'NO'
  const [shippingDoorComment, setShippingDoorComment] = useState('');

  const [notes, setNotes] = useState('');
  const [topError, setTopError] = useState(null);
  const [success, setSuccess]   = useState(null);

  const unprintedCount = unprintedQ.data?.count ?? 0;

  // Effective print flags: a field only counts as "printed" when the
  // master personalise toggle is YES. This keeps the downstream logic
  // (validation, spare-qty, payload) honest regardless of stale
  // checkbox state left over from a YES→NO flip.
  const personalizing = personalize === 'YES';
  const effPrintName  = personalizing && printName;
  const effPrintClass = personalizing && printClass;
  const effPrintPhone = personalizing && printPhone;

  // Auto-suggest named qty when name-printing turns on — saves the
  // manager from typing it and avoids the validation slap.
  useEffect(() => {
    if (effPrintName && unprintedCount > 0 && !qtyNamed) {
      setQtyNamed(String(unprintedCount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effPrintName, unprintedCount]);

  const qN = parseInt(qtyNamed, 10) || 0;
  // Spare cards are only meaningful as an add-on to a named-print order
  // (extras without personalisation). When names aren't printed, every
  // card is unnamed by definition and the second input would be noise.
  const qS = effPrintName ? (parseInt(qtySpare, 10) || 0) : 0;
  const total = qN + qS;

  // Live pricing
  const cardCost = total * PRICE_PER_CARD;
  const shippingCost = shippingMethod === 'EXPRESS' ? SHIPPING_EXPRESS : 0;
  const grandTotal = +(cardCost + shippingCost).toFixed(2);

  // Derived validation hints (mirror the server, but soft — submit
  // still works; the server is the authority).
  const namedMismatch = effPrintName && qN !== unprintedCount && qN > 0;
  const belowMin = total > 0 && total < MIN_TOTAL;

  function buildTotalPriceText() {
    const parts = [`מחיר לכרטיס: ₪${PRICE_PER_CARD}`];
    parts.push(`סה"כ עלות כרטיסים: ₪${cardCost}`);
    if (shippingCost > 0) parts.push(`משלוח: ₪${shippingCost}`);
    parts.push(`סה"כ לתשלום: ₪${grandTotal} + מע"מ`);
    return parts.join(' | ');
  }

  // Validation failures used to silently set topError while the user
  // was still scrolled at the sticky-bottom action bar — they tapped
  // submit, nothing visible happened, they had to scroll up to find
  // out why. `fail` now sets the error AND scrolls the banner into
  // view so the failure is always discoverable from anywhere on the
  // page.
  function fail(msg) {
    setTopError(msg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return false;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTopError(null);

    // Client-side guards — friendlier than waiting for the server.
    if (!schoolDisplay) return fail('יש לבחור מה להציג בכרטיס מצד הארגון.');
    if (schoolDisplay === 'NAME' && !schoolName.trim()) return fail('יש להזין שם ארגון להדפסה.');
    if (schoolDisplay === 'LOGO' && !logoFile) return fail('יש להעלות קובץ לוגו.');
    if (!personalize) return fail('יש לבחור אם להדפיס פרטי תלמיד על הכרטיס.');
    if (personalizing && !printName && !printClass && !printPhone) {
      return fail('בחרת להדפיס פרטי תלמיד — יש לסמן לפחות פרט אחד (שם / כיתה / טלפון).');
    }
    if (total < MIN_TOTAL) return fail(`מינימום ההזמנה הוא ${MIN_TOTAL} כרטיסים.`);
    if (effPrintName && qN !== unprintedCount) {
      return fail(`הזמנת ${qN} כרטיסים עם שם, אבל יש ${unprintedCount} תלמידים ללא כרטיס. הכמות חייבת להיות תואמת.`);
    }
    if (!inquiryPhone.trim()) return fail('יש להזין טלפון לבירורים.');
    if (!shippingMethod) return fail('יש לבחור סוג משלוח.');
    if (shippingMethod === 'EXPRESS') {
      if (!shippingAddress.trim()) return fail('יש להזין כתובת למשלוח.');
      if (!shippingCity.trim())    return fail('יש להזין עיר.');
      if (!shippingRecipient.trim()) return fail('יש להזין שם מקבל החבילה.');
      if (!shippingPhone.trim())  return fail('יש להזין טלפון מקבל החבילה.');
      if (!shippingPhone2.trim()) return fail('יש להזין טלפון נוסף למשלוח אקספרס.');
      if (!shippingDoorOk)        return fail('יש להחליט אם אפשר להשאיר את החבילה ליד הדלת.');
      if (shippingDoorOk === 'YES' && !shippingDoorComment.trim()) {
        return fail('יש לפרט איפה להשאיר את החבילה.');
      }
    }
    const fd = new FormData();
    fd.append('schoolDisplay', schoolDisplay);
    if (schoolDisplay === 'NAME') fd.append('schoolDisplayName', schoolName.trim());
    if (schoolDisplay === 'LOGO' && logoFile) fd.append('logo', logoFile);

    fd.append('printName',  effPrintName  ? 'true' : 'false');
    fd.append('printClass', effPrintClass ? 'true' : 'false');
    fd.append('printPhone', effPrintPhone ? 'true' : 'false');

    fd.append('qtyNamed', String(qN));
    fd.append('qtySpare', String(qS));

    fd.append('inquiryPhone',  inquiryPhone.trim());
    fd.append('shippingMethod', shippingMethod);
    if (shippingMethod === 'EXPRESS') {
      fd.append('shippingAddress',     shippingAddress.trim());
      fd.append('shippingCity',        shippingCity.trim());
      fd.append('shippingRecipient',   shippingRecipient.trim());
      fd.append('shippingPhone',       shippingPhone.trim());
      fd.append('shippingPhone2',      shippingPhone2.trim());
      fd.append('shippingDoorOk',      shippingDoorOk);
      if (shippingDoorOk === 'YES') fd.append('shippingDoorComment', shippingDoorComment.trim());
    }

    if (notes.trim()) fd.append('notes', notes.trim());
    fd.append('totalPriceText', buildTotalPriceText());

    try {
      const result = await createMut.mutateAsync(fd);
      setSuccess(result);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'named_qty_mismatch' || err.code === 'no_unprinted_students') {
          setTopError(err.message);
        } else if (err.code === 'logo_too_large') {
          setTopError('קובץ הלוגו גדול מדי. מקסימום 5MB.');
        } else if (err.code === 'logo_invalid_type') {
          setTopError('סוג הקובץ של הלוגו לא נתמך. תמונה (PNG/JPG/SVG) בלבד.');
        } else if (err.status === 422 && Array.isArray(err.details)) {
          setTopError(`שגיאה בנתונים: ${err.details[0]?.message || 'יש לבדוק את הטופס.'}`);
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. נסו שוב.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  if (camp.isLoading) return <Spinner label="טוען תכנית..." />;
  if (camp.isError || !camp.data) {
    return (
      <div className="card-danger p-5 text-sm">
        <p className="text-joy-900 font-medium">שגיאה: {camp.error?.message || 'תכנית לא נמצאה'}</p>
        <Link to="/" className="btn-secondary btn-sm mt-3">חזרה לרשימה</Link>
      </div>
    );
  }

  if (success) {
    return <SuccessScreen campId={campId} campName={camp.data.name} result={success} />;
  }

  return (
    <div className="space-y-6 pb-32">
      {/* Hero */}
      <header className="card-pop p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gold-gradient" />
        <Link
          to={`/camps/${campId}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-900"
        >
          <span aria-hidden>←</span> חזרה לתכנית
        </Link>
        <div className="mt-3 flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-gold-gradient text-white grid place-items-center shrink-0 shadow-pop">
            <CreditCardIcon className="w-7 h-7" />
          </div>
          <div className="min-w-0">
            <p className="eyebrow">הזמנת ציוד</p>
            <h1 className="h-page">הזמנת כרטיסים מגנטיים</h1>
            <p className="mt-1.5 text-muted">
              עבור התכנית: <strong className="text-ink-900">{camp.data.name}</strong>.
              ההזמנה נשלחת ל-Nedarim Plus, התשלום נגבה דרך אמצעי התשלום הרשומים במערכת.
            </p>
          </div>
        </div>
      </header>

      {/* Past orders for this camp — shown above the form so the manager
          can see what they already ordered + the current status before
          submitting another order. */}
      {ordersQ.data?.orders?.length > 0 && (
        <PastOrders campId={campId} orders={ordersQ.data.orders} />
      )}

      {topError && (
        <div role="alert" className="card-danger p-4 flex items-start gap-2 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5 text-joy-700" />
          <span className="text-joy-900 font-medium">{topError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* SCHOOL IDENTITY */}
        <Section title="פרטי הארגון" hint="מה יודפס בכרטיס מצד הארגון" required>
          <RadioRow
            name="schoolDisplay"
            value={schoolDisplay}
            onChange={(v) => { setSchoolDisplay(v); if (v !== 'LOGO') setLogoFile(null); }}
            options={[
              { value: 'NAME', label: 'הצג שם ארגון' },
              { value: 'LOGO', label: 'הצג לוגו' },
              { value: 'NONE', label: 'לא להציג' },
            ]}
          />
          {schoolDisplay === 'NAME' && (
            <label className="block mt-3">
              <ReqLabel>שם הארגון להדפסה</ReqLabel>
              <input
                type="text"
                className="input"
                maxLength={50}
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
              />
            </label>
          )}
          {schoolDisplay === 'LOGO' && (
            <div className="mt-3 space-y-2">
              <ReqLabel>קובץ לוגו</ReqLabel>
              <div className="hint text-joy-700">שימו לב! ההדפסה תתבצע בשחור-לבן בלבד.</div>
              <LogoDropzone file={logoFile} onChange={setLogoFile} />
            </div>
          )}
        </Section>

        {/* CARDHOLDER FIELDS — master toggle first, then sub-choices */}
        <Section title="פרטי בעל הכרטיס" hint="האם להדפיס פרטים אישיים על הכרטיס" required>
          <RadioRow
            name="personalize"
            value={personalize}
            onChange={(v) => {
              setPersonalize(v);
              if (v === 'NO') {
                // Reset sub-choices so generic cards don't accidentally
                // carry over a stale name/class/phone selection.
                setPrintName(false); setPrintClass(false); setPrintPhone(false);
                setQtySpare('');
              } else if (v === 'YES') {
                setPrintName(true); // sensible default — most orders print the name
              }
            }}
            options={[
              { value: 'YES', label: 'כן, להדפיס פרטי תלמיד' },
              { value: 'NO',  label: 'לא, כרטיסים גנריים' },
            ]}
          />

          {personalizing && (
            <div className="mt-4 space-y-2">
              <div className="text-xs font-bold text-ink-500 uppercase tracking-wider">מה להדפיס?</div>
              <CheckRow checked={printName}  onChange={setPrintName}  label="שם בעל הכרטיס (פרטי + משפחה)" />
              <CheckRow checked={printClass} onChange={setPrintClass} label="כיתה" />
              <CheckRow checked={printPhone} onChange={setPrintPhone} label="טלפון" />
              {printName && (
                <div className="text-xs text-joy-700 pr-2 leading-relaxed pt-1">
                  שימו לב: יש להעלות את התלמידים לטבלת ניהול התלמידים לפני הזמנת
                  כרטיסים עם שמות. במאגר התכנית נמצאים{' '}
                  <strong className="text-ink-900 num">{unprintedCount.toLocaleString('he-IL')}</strong>{' '}
                  תלמידים ללא כרטיס מוגדר. כמות הכרטיסים עם השם חייבת להיות תואמת.
                </div>
              )}
            </div>
          )}
        </Section>

        {/* PRICING NOTE */}
        <Section title="מחירון" hint="כמו בדף הבית — שקוף וקבוע">
          <ul className="text-sm text-ink-700 leading-relaxed list-disc pr-5 space-y-0.5">
            <li>עלות לכרטיס: <strong className="num text-ink-900">₪{PRICE_PER_CARD}</strong> + מע"מ (חד-פעמי)</li>
            <li>שם בית הספר / לוגו / שם תלמיד — הכל ללא תוספת תשלום</li>
          </ul>
        </Section>

        {/* QUANTITIES */}
        <Section title="כמויות" required>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <ReqLabel>
                כמות כרטיסים {effPrintName && <span className="text-ink-400 font-normal">(עם פרטים)</span>}
              </ReqLabel>
              <input
                type="text"
                inputMode="numeric"
                className="input num"
                dir="ltr"
                maxLength={5}
                value={qtyNamed}
                onChange={(e) => setQtyNamed(e.target.value.replace(/\D/g, ''))}
              />
              {namedMismatch && (
                <span className="hint text-joy-700">
                  לא תואם — יש {unprintedCount} תלמידים ללא כרטיס.
                </span>
              )}
            </label>
            {effPrintName && (
              <label className="block">
                <span className="label">כמות ספייר (ללא פרטים)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input num"
                  dir="ltr"
                  maxLength={5}
                  value={qtySpare}
                  onChange={(e) => setQtySpare(e.target.value.replace(/\D/g, ''))}
                />
                <span className="hint">אופציונלי. כרטיסים ריקים כתוספת.</span>
              </label>
            )}
          </div>
          {belowMin && (
            <div className="hint text-joy-700 mt-2">
              מינימום ההזמנה הוא {MIN_TOTAL} כרטיסים סה"כ.
            </div>
          )}
        </Section>

        {/* INQUIRY */}
        <Section title="טלפון לבירורים" required>
          <input
            type="tel"
            className="input num"
            dir="ltr"
            maxLength={11}
            value={inquiryPhone}
            onChange={(e) => setInquiryPhone(e.target.value)}
          />
        </Section>

        {/* SHIPPING */}
        <Section title="סוג משלוח" required>
          <RadioRow
            name="shippingMethod"
            value={shippingMethod}
            onChange={setShippingMethod}
            options={[
              { value: 'PICKUP',  label: 'איסוף עצמי מנתיבות (ללא עלות, בתיאום מראש)' },
              { value: 'EXPRESS', label: 'דואר אקספרס · ₪60 + מע"מ' },
            ]}
          />
          {shippingMethod === 'EXPRESS' && (
            <div className="mt-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <ReqLabel>כתובת</ReqLabel>
                  <input type="text" className="input" maxLength={150}
                         value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} />
                </label>
                <label className="block">
                  <ReqLabel>עיר</ReqLabel>
                  <input type="text" className="input" maxLength={80}
                         value={shippingCity} onChange={(e) => setShippingCity(e.target.value)} />
                </label>
                <label className="block">
                  <ReqLabel>שם מקבל החבילה</ReqLabel>
                  <input type="text" className="input" maxLength={80}
                         value={shippingRecipient} onChange={(e) => setShippingRecipient(e.target.value)} />
                </label>
                <label className="block">
                  <ReqLabel>טלפון מקבל</ReqLabel>
                  <input type="tel" className="input num" dir="ltr" maxLength={11}
                         value={shippingPhone} onChange={(e) => setShippingPhone(e.target.value)} />
                </label>
                <label className="block sm:col-span-2">
                  <ReqLabel>טלפון נוסף — מקבל החבילה</ReqLabel>
                  <input type="tel" className="input num" dir="ltr" maxLength={11}
                         value={shippingPhone2} onChange={(e) => setShippingPhone2(e.target.value)} />
                </label>
              </div>
              <div>
                <ReqLabel className="block mb-1.5">
                  האם אפשר להשאיר את החבילה ליד הדלת?
                  <span className="block text-xs text-joy-700 font-normal mt-0.5">
                    אם לא יהיה מענה והשליח לא יוכל למסור — תחויבו בעלות משלוח נוספת.
                  </span>
                </ReqLabel>
                <RadioRow
                  name="door"
                  value={shippingDoorOk}
                  onChange={setShippingDoorOk}
                  options={[
                    { value: 'YES', label: 'מאשר' },
                    { value: 'NO',  label: 'לא מאשר' },
                  ]}
                />
              </div>
              {shippingDoorOk === 'YES' && (
                <label className="block">
                  <ReqLabel>נא לפרט איפה להשאיר (ארון חשמל / ליד הדלת / וכו׳)</ReqLabel>
                  <textarea className="input" rows={2} maxLength={300}
                            value={shippingDoorComment}
                            onChange={(e) => setShippingDoorComment(e.target.value)} />
                </label>
              )}
            </div>
          )}
        </Section>

        {/* NOTES */}
        <Section title="הערות להזמנה">
          <textarea className="input" rows={3} maxLength={500}
                    value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Section>

        <div className="text-xs text-ink-500 px-1">
          התשלום עבור ההזמנה ייגבה מאמצעי התשלום הרשומים במערכות Nedarim Plus
          (מטרה הפקות בע״מ), ולא יקוזז מיתרת התלמידים.
        </div>
      </form>

      {/* STICKY BOTTOM SUMMARY BAR */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-ink-200 shadow-pop">
        <div className="max-w-3xl mx-auto p-4 flex items-center gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-ink-500 font-bold uppercase tracking-wider">סה"כ לתשלום</div>
            <div className="font-display font-extrabold text-2xl text-ink-900 num leading-none mt-0.5">
              ₪{grandTotal.toLocaleString('he-IL')} <span className="text-base text-ink-500 font-bold">+ מע"מ</span>
            </div>
            <div className="text-[11px] text-ink-500 mt-0.5 num">
              {total.toLocaleString('he-IL')} כרטיסים × ₪{PRICE_PER_CARD}
              {shippingCost > 0 && <> + משלוח ₪{shippingCost}</>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMut.isPending}
              className="btn-pop btn-lg whitespace-nowrap"
            >
              {createMut.isPending ? 'שולח...' : 'סיום הזמנה'}
            </button>
            {topError && (
              <span className="text-[11px] text-joy-700 font-bold flex items-center gap-1">
                <ExclamationTriangleIcon className="w-3 h-3" />
                יש שדה שלא הושלם — גלול למעלה
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── helpers ─────────────────────────────────────────── */

const STATUS_LABEL = {
  SUBMITTED:  { text: 'נשלחה',     cls: 'badge-amber' },
  PROCESSING: { text: 'בעיבוד',    cls: 'badge-sky' },
  SHIPPED:    { text: 'נשלחה אליך', cls: 'badge-mint' },
  CANCELLED:  { text: 'בוטלה',     cls: 'badge-slate' },
};

function PastOrders({ campId, orders }) {
  // Collapsed by default — only the manager who wants to review prior
  // orders expands it, so it doesn't push the form down on every visit.
  const [open, setOpen] = useState(false);
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 bg-ink-50/50 hover:bg-ink-50 transition-colors flex items-center justify-between gap-3 text-start"
        aria-expanded={open}
      >
        <div>
          <h2 className="font-bold text-ink-900">הזמנות קודמות</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            {open ? 'סטטוס כל הזמנות הכרטיסים בתוכנית הזו' : 'לחצו להצגת ההזמנות שכבר בוצעו'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="badge-slate num text-xs">{orders.length}</span>
          <svg
            className={`w-4 h-4 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
      {open && (
        <ul className="divide-y divide-ink-100 border-t border-ink-100 animate-fade-in">
          {orders.map((o) => <PastOrderRow key={o.id} campId={campId} o={o} />)}
        </ul>
      )}
    </div>
  );
}

function PastOrderRow({ campId, o }) {
  const st = STATUS_LABEL[o.status] || STATUS_LABEL.SUBMITTED;
  return (
    <li className="px-5 py-3 flex items-center gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-ink-900 num">
            {o.qtyTotal.toLocaleString('he-IL')} כרטיסים
          </span>
          {o.printName && <span className="badge-slate text-xs">עם שמות</span>}
          {o.qtySpare > 0 && <span className="badge-slate text-xs num">+{o.qtySpare} ספייר</span>}
          <span className={`${st.cls} text-xs`}>{st.text}</span>
        </div>
        <div className="text-xs text-ink-500 mt-0.5 flex flex-wrap items-center gap-x-2 num">
          <span>{formatDateTime(o.createdAt)}</span>
          <span aria-hidden>·</span>
          <span>ע״י {o.createdBy?.name || o.createdBy?.email || '—'}</span>
          {o.shippingMethod === 'EXPRESS' && (
            <>
              <span aria-hidden>·</span>
              <span>אקספרס</span>
            </>
          )}
          {o.hasLogo ? (
            <>
              <span aria-hidden>·</span>
              <a
                href={`/api/camps/${campId}/card-orders/${o.id}/logo`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-brand-700 hover:text-brand-900 underline decoration-brand-200 decoration-2 underline-offset-2"
              >
                לוגו: {o.logoFilename}
              </a>
            </>
          ) : o.logoFilename ? (
            <>
              <span aria-hidden>·</span>
              <span className="text-ink-400">לוגו: {o.logoFilename} (לא נשמר)</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="text-end shrink-0">
        <div className="text-xs text-ink-500">מזהה</div>
        <code className="font-mono text-[11px] text-ink-700" dir="ltr">
          {o.id.slice(0, 8)}…
        </code>
      </div>
    </li>
  );
}

function Section({ title, hint, required, children }) {
  return (
    <div className="card p-5 sm:p-6">
      <div className="mb-3">
        <h2 className="font-bold text-ink-900 text-lg flex items-center gap-1.5">
          {title}
          {required && <span className="text-joy-600" aria-label="חובה">*</span>}
        </h2>
        {hint && <p className="text-xs text-ink-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function ReqLabel({ children, className = '' }) {
  return (
    <span className={`label ${className}`}>
      {children} <span className="text-joy-600" aria-label="חובה">*</span>
    </span>
  );
}

function RadioRow({ name, value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={
            value === opt.value
              ? 'chip-active cursor-pointer'
              : 'chip cursor-pointer'
          }
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={(e) => onChange(e.target.value)}
            className="sr-only"
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckRow({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-2xl bg-ink-50/60 hover:bg-ink-50 cursor-pointer transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-brand-600"
      />
      <span className="text-sm text-ink-800 font-medium">{label}</span>
    </label>
  );
}

function LogoDropzone({ file, onChange }) {
  const inputRef = useRef(null);
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full p-5 rounded-2xl border-2 border-dashed border-ink-300 hover:border-brand-300 hover:bg-brand-50/50 transition-colors flex items-center justify-center gap-2 text-ink-600"
        >
          <ArrowDownTrayIcon className="w-5 h-5 rotate-180" />
          <span className="font-semibold">בחר קובץ לוגו</span>
          <span className="text-xs text-ink-400">PNG / JPG / SVG · עד 5MB</span>
        </button>
      ) : (
        <div className="flex items-center justify-between gap-2 p-3 rounded-2xl border border-mint-200 bg-mint-50">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-ink-900 truncate">{file.name}</div>
            <div className="text-xs text-ink-500">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <button type="button" onClick={() => onChange(null)} className="btn-secondary btn-sm">
            הסר
          </button>
        </div>
      )}
    </div>
  );
}

function SuccessScreen({ campId, campName, result }) {
  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="card-pop p-7 sm:p-10 text-center relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-2 bg-mint-gradient" />
        <div className="mx-auto w-20 h-20 rounded-3xl bg-mint-gradient text-white grid place-items-center mb-4 shadow-pop">
          <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h1 className="font-display font-extrabold text-3xl text-ink-900">
          ההזמנה נקלטה
        </h1>
        <p className="mt-2 text-muted">
          הזמנה ל-<strong className="text-ink-900">{campName}</strong> נשלחה לעיבוד.
          {result?.emailDelivered
            ? ' מייל אישור נשלח אליך ולמשרד Nedarim Plus.'
            : ' שמרנו את ההזמנה — תקלה זמנית בשליחת המייל, צוות Nedarim Plus יבדוק וייצור איתך קשר.'}
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mint-50 border border-mint-200">
          <span className="text-xs font-bold uppercase text-mint-700">מזהה הזמנה</span>
          <code className="font-mono text-sm text-ink-900" dir="ltr">{result?.orderId}</code>
        </div>
        <div className="mt-7 flex gap-2 justify-center flex-wrap">
          <Link to={`/camps/${campId}`} className="btn-secondary">חזרה לתכנית</Link>
          <Link to={`/camps/${campId}/order-cards`} className="btn-pop" onClick={() => window.location.reload()}>
            הזמנה נוספת
          </Link>
        </div>
      </div>
    </div>
  );
}
